import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Navbar from '../../../components/organism/navbar/index.tsx'
import Footer from '../../../components/organism/footer/index.tsx'
import WeddingPlanerHero from '../../../components/organism/weddingPlanerHero/index.tsx'
import WeddingPlanerFeatures from '../../../components/organism/weddingPlanerFeatures/index.tsx'
import WeddingPlanerHowItWorks from '../../../components/organism/weddingPlanerHowItWorks/index.tsx'
import ContactForm from '../../../components/organism/contactForm/index.tsx'
import PlanerBtn from '../../../components/atom/planer-btn/index.tsx'
import MarketingSeo from '../../../components/marketingSeo/index.tsx'
import './index.css'

interface AccordionItemProps {
	title: string
	text: string
	isOpen: boolean
	onClick: () => void
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, text, isOpen, onClick }) => (
	<div className={`planer-faq-item ${isOpen ? 'open' : ''}`} onClick={onClick}>
		<div className='planer-faq-item-header'>
			<h3>{title}</h3>
			<svg
				className={`planer-faq-arrow ${isOpen ? 'rotated' : ''}`}
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path d="M6 9L12 15L18 9" stroke="#282828" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
			</svg>
		</div>
		{isOpen && (
			<div className='planer-faq-item-content'>
				<p>{text}</p>
			</div>
		)}
	</div>
)

const WeddingPlanerLanding: React.FC = () => {
	const navigate = useNavigate()
	const { t } = useTranslation('planner')
	const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
	const contactRef = useRef<HTMLDivElement>(null)
	const faqItems = t('landing.faq.items', { returnObjects: true }) as { title: string; text: string }[]

	useEffect(() => {
		document.querySelector('.body-container')?.classList.add('position-initial')
		setTimeout(() => {
			window.scrollTo({ top: 0 })
		}, 200)
	}, [])

	const scrollToContact = () => {
		contactRef.current?.scrollIntoView({ behavior: 'smooth' })
	}

	const handleFaqClick = (index: number) => {
		setOpenFaqIndex(openFaqIndex === index ? null : index)
	}

	return (
		<div className='body-container planer-landing-container'>
			<MarketingSeo page='planner' />
			<Navbar scrollToContact={scrollToContact} />
			<WeddingPlanerHero />
			<WeddingPlanerFeatures />
			<WeddingPlanerHowItWorks />

			{/* FAQ Section */}
			<section className='planer-faq-section'>
				<div className='planer-faq-header'>
					<h2 className='planer-faq-title'>{t('landing.faq.title')}</h2>
					<p className='planer-faq-subtitle'>{t('landing.faq.subtitle')}</p>
				</div>

				<div className='planer-faq-list'>
					{faqItems.map((item, index) => (
						<AccordionItem
							key={index}
							title={item.title}
							text={item.text}
							isOpen={openFaqIndex === index}
							onClick={() => handleFaqClick(index)}
						/>
					))}
				</div>

				<div className='planer-faq-cta'>
					<p>{t('landing.faq.haveQuestion')}</p>
					<PlanerBtn
						title={t('landing.faq.contactUs')}
						onClick={scrollToContact}
						variant='secondary'
					/>
				</div>
			</section>

			{/* CTA Banner */}
			<section className='planer-cta-banner'>
				<div className='planer-cta-banner-content'>
					<h2>{t('landing.ctaBanner.title')}</h2>
					<p>{t('landing.ctaBanner.subtitle')}</p>
					<div className='planer-cta-demo-pin'>
						<span>{t('landing.ctaBanner.demoPin')}</span>
						<code>12345678</code>
					</div>
					<PlanerBtn
						title={t('landing.ctaBanner.cta')}
						onClick={() => navigate('/weddingPlanner/login')}
						size='large'
					/>
				</div>
			</section>

			<div ref={contactRef}>
				<ContactForm
					title={t('landing.contact.title')}
					subtitle={t('landing.contact.subtitle')}
				/>
			</div>

			<Footer />
		</div>
	)
}

export default WeddingPlanerLanding
